// Inspired by Espen Garder Haug http://www.espenhaug.com/black_scholes.html and https://gist.github.com/santacruz123/3623310
// ported to JavaScript by Stefan Wilhelm <stefan.wilhelm@financial.com>
// ported to ES6 by fsh <fsh@financial.com>

/**
 * The cummulative Normal distribution function
 *
 * @param {Number} x
 * @return ?
 */
const CND = x => {
  if (x < 0) {
    return 1 - CND(-x);
  } else {
    const k = 1 / (1 + 0.2316419 * x);
    return (
      1 -
      (Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI)) *
        k *
        (0.31938153 + k * (-0.356563782 + k * (1.781477937 + k * (-1.821255978 + k * 1.330274429))))
    );
  }
};

/**
 * Normal density functions
 *
 * @param {Number} x
 * @return ?
 */
const NDF = x => Math.exp((-x * x) / 2) / Math.sqrt(8 * Math.atan(1));

/**
 * Calculate Generalized Black-Scholes option price
 *
 * @param {String} optionType (C || P)
 * @param {String} S stock price
 * @param {String} X strike price
 * @param {Number} Time time to expiration (in years)
 * @param {Number} r risk-free rate
 * @param {Number} b cost of carry rate
 * @param {Number} sigma volatility
 */
function GBSOption(optionType, S, X, Time, r, b, sigma) {
  const d1 = (Math.log(S / X) + (b + (sigma * sigma) / 2) * Time) / (sigma * Math.sqrt(Time));
  const d2 = d1 - sigma * Math.sqrt(Time);

  return optionType === 'C'
    ? S * Math.exp((b - r) * Time) * CND(d1) - X * Math.exp(-r * Time) * CND(d2)
    : X * Math.exp(-r * Time) * CND(-d2) - S * Math.exp((b - r) * Time) * CND(-d1);
}

/**
 * Calculate DELTA for Generalized Black-Scholes
 *
 * @param {String} optionType (C || P)
 * @param {String} S stock price
 * @param {String} X strike price
 * @param {Number} Time time to expiration (in years)
 * @param {Number} r risk-free rate
 * @param {Number} b cost of carry rate
 * @param {Number} sigma volatility
 */
function GBSDelta(optionType, S, X, Time, r, b, sigma) {
  const d1 = (Math.log(S / X) + (b + (sigma * sigma) / 2) * Time) / (sigma * Math.sqrt(Time));

  return optionType === 'C' ? Math.exp((b - r) * Time) * CND(d1) : Math.exp((b - r) * Time) * (CND(d1) - 1);
}

/**
 * Calculate GAMMA for Generalized Black-Scholes
 *
 * @param {String} optionType (C || P)
 * @param {String} S stock price
 * @param {String} X strike price
 * @param {Number} Time time to expiration (in years)
 * @param {Number} r risk-free rate
 * @param {Number} b cost of carry rate
 * @param {Number} sigma volatility
 */
function GBSGamma(optionType, S, X, Time, r, b, sigma) {
  const d1 = (Math.log(S / X) + (b + (sigma * sigma) / 2) * Time) / (sigma * Math.sqrt(Time));

  return (Math.exp((b - r) * Time) * NDF(d1)) / (S * sigma * Math.sqrt(Time));
}

/**
 * Calculate THETA for Generalized Black-Scholes
 *
 * @param {String} optionType (C || P)
 * @param {String} S stock price
 * @param {String} X strike price
 * @param {Number} Time time to expiration (in years)
 * @param {Number} r risk-free rate
 * @param {Number} b cost of carry rate
 * @param {Number} sigma volatility
 */
function GBSTheta(optionType, S, X, Time, r, b, sigma) {
  const d1 = (Math.log(S / X) + (b + (sigma * sigma) / 2) * Time) / (sigma * Math.sqrt(Time));
  const d2 = d1 - sigma * Math.sqrt(Time);
  const Theta1 = -(S * Math.exp((b - r) * Time) * NDF(d1) * sigma) / (2 * Math.sqrt(Time));

  return optionType === 'C'
    ? Theta1 - (b - r) * S * Math.exp((b - r) * Time) * CND(+d1) - r * X * Math.exp(-r * Time) * CND(+d2)
    : Theta1 + (b - r) * S * Math.exp((b - r) * Time) * CND(-d1) + r * X * Math.exp(-r * Time) * CND(-d2);
}

/**
 * Calculate RHO for Generalized Black-Scholes
 *
 * @param {String} optionType (C || P)
 * @param {String} S stock price
 * @param {String} X strike price
 * @param {Number} Time time to expiration (in years)
 * @param {Number} r risk-free rate
 * @param {Number} b cost of carry rate
 * @param {Number} sigma volatility
 */
function GBSRho(optionType, S, X, Time, r, b, sigma) {
  const d1 = (Math.log(S / X) + (b + (sigma * sigma) / 2) * Time) / (sigma * Math.sqrt(Time));
  const d2 = d1 - sigma * Math.sqrt(Time);
  const CallPut = GBSOption(optionType, S, X, Time, r, b, sigma);

  return optionType === 'C'
    ? b !== 0
      ? Time * X * Math.exp(-r * Time) * CND(d2)
      : -Time * CallPut
    : b !== 0
    ? -Time * X * Math.exp(-r * Time) * CND(-d2)
    : -Time * CallPut;
}

/**
 * Calculate VEGA for Generalized Black-Scholes
 *
 * @param {String} optionType (C || P)
 * @param {String} S stock price
 * @param {String} X strike price
 * @param {Number} Time time to expiration (in years)
 * @param {Number} r risk-free rate
 * @param {Number} b cost of carry rate
 * @param {Number} sigma volatility
 */
function GBSVega(optionType, S, X, Time, r, b, sigma) {
  const d1 = (Math.log(S / X) + (b + (sigma * sigma) / 2) * Time) / (sigma * Math.sqrt(Time));
  return S * Math.exp((b - r) * Time) * NDF(d1) * Math.sqrt(Time);
}

export const greeksCalculations = {
  GBSOption,
  GBSDelta,
  GBSGamma,
  GBSTheta,
  GBSRho,
  GBSVega
};
